import type { EnvironmentConfig, FlightFrame, ShotResult, SwingParams } from '../types';
import { M_TO_YARDS } from '../constants';

export type PatternSource = 'modeled' | 'measured-launches';
export interface PatternShot {
  sampleId: number;
  result: ShotResult;
  spinRate: number;
}
export interface ShotPattern {
  source: PatternSource;
  cohortId: string;
  shots: PatternShot[];
  /** One paired sample (index zero), identical delivery in every replay. */
  replay: FlightFrame[];
  typicalCarry: number;
  typicalTotal: number;
  landingWidth: number;
  badMiss: number;
  carryWidth: number;
  typicalOffline: number;
}
export interface PatternRequest {
  id: string;
  cohortId: string;
  source: PatternSource;
  swings: SwingParams[];
  environment: EnvironmentConfig;
}

export function quantile(values: readonly number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const i = (sorted.length - 1) * p;
  return sorted[Math.floor(i)] + (sorted[Math.ceil(i)] - sorted[Math.floor(i)]) * (i % 1);
}

export function summarizePattern(shots: PatternShot[], replay: FlightFrame[], source: PatternSource, cohortId: string): ShotPattern {
  if (!shots.length) throw new Error('Empty shot pattern');
  const carry = shots.map(s => s.result.carryDistance * M_TO_YARDS);
  const side = shots.map(s => s.result.landingPosition.x * M_TO_YARDS);
  return {
    source, cohortId, shots, replay,
    typicalCarry: quantile(carry, .5),
    typicalTotal: quantile(shots.map(s => s.result.totalDistance * M_TO_YARDS), .5),
    landingWidth: quantile(side, .9) - quantile(side, .1),
    badMiss: quantile(side.map(Math.abs), .9),
    carryWidth: quantile(carry, .9) - quantile(carry, .1),
    typicalOffline: quantile(shots.map(s => Math.abs(s.result.offline) * M_TO_YARDS), .5),
  };
}

/** Preserve each observed/modelled delivery; apply the same equipment response
 * to each. This is a counterfactual, not a claim of measured equipment benefit. */
export function pairedEquipmentSwings(swings: readonly SwingParams[], reference: SwingParams, adjusted: SwingParams): SwingParams[] {
  return swings.map(s => ({ ...s,
    ballSpeed: s.ballSpeed * (reference.ballSpeed > 0 ? adjusted.ballSpeed / reference.ballSpeed : 1),
    launchAngle: s.launchAngle + (adjusted.launchAngle - reference.launchAngle),
    spinRate: Math.max(0, reference.spinRate > 0 ? s.spinRate * (adjusted.spinRate / reference.spinRate)
      : s.spinRate + adjusted.spinRate - reference.spinRate),
  }));
}

export function seededPatternRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return (state + .5) / 4294967296;
  };
}
